/**
 * The postMessage contract between the launcher (`<gasbi-agents-chat>` in a Gasbi app, `launcher.js`) and the
 * embedded chat (`/embed` in its iframe) — ADR-0004 of Gasbi.Agents §2.7. Every message is an object whose `type`
 * starts with `gasbi-ai:`; no token or secret ever travels in one. The launcher accepts messages only from its own
 * iframe and the chat's origin; the chat accepts them only from `window.parent`, remembers the origin of the first
 * `init` and answers only to it (`frame-ancestors` already limits who may frame the chat).
 */
export const MESSAGE_TYPE_PREFIX = 'gasbi-ai:';

/** What the host app knows and the chat needs: both may change while the panel lives. */
export interface ChatHostContext {
  /**
   * The organization chosen in the host app: the chat's own session switches to it (GSB-8274) and, meanwhile, it goes
   * out as the `organizationId` header.
   */
  organizationId?: string;
  /** The `sub` of the user signed in to the host app; the chat switches its session when it differs. */
  userSub?: string;
}

export interface ChatInit extends ChatHostContext {
  /** The app key of the panel (`supply`, `crm`, …): the chat lists only that app's agents and conversations. */
  app: string;
  theme?: 'light' | 'dark';
  lang?: string;
}

/** Launcher → chat: `init` answers the chat's `ready`, `context` follows every change of the element's attributes. */
export type HostToChatMessage =
  | ({ type: 'gasbi-ai:init' } & ChatInit)
  | ({ type: 'gasbi-ai:context' } & ChatHostContext);

/** Chat → launcher: `ready` once the app has started, `close` from the panel's own close button, `unread` after an answer. */
export type ChatToHostMessage =
  | { type: 'gasbi-ai:ready' }
  | { type: 'gasbi-ai:close' }
  | { type: 'gasbi-ai:unread'; count: number };

const hasType = (data: unknown): data is { type: string } =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as { type?: unknown }).type === 'string';

export const isHostToChatMessage = (data: unknown): data is HostToChatMessage =>
  hasType(data) &&
  (data.type === 'gasbi-ai:init' || data.type === 'gasbi-ai:context');

export const isChatToHostMessage = (data: unknown): data is ChatToHostMessage =>
  hasType(data) &&
  (data.type === 'gasbi-ai:ready' ||
    data.type === 'gasbi-ai:close' ||
    data.type === 'gasbi-ai:unread');
